import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { documentService, signatureService } from '../../services/api';
import { FileText, Upload, Download, Loader2, File, PenLine, AlertCircle } from 'lucide-react';
import SignDocumentDialog from '../../components/documents/SignDocumentDialog';

/**
 * Dashboard per clienti
 */
function ClientDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [documents, setDocuments] = useState([]);
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [signDialog, setSignDialog] = useState({ open: false, document: null });

  useEffect(() => {
    loadDocuments();
    loadPendingSignatures();
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await documentService.getAll({ limit: 10 });
      setDocuments(response.data?.documents || []);
    } catch (error) {
      console.error('Errore caricamento documenti:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPendingSignatures = async () => {
    try {
      const response = await signatureService.getPending();
      setPendingSignatures(response.data?.documents || []);
    } catch (error) {
      console.error('Errore caricamento firme pendenti:', error);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentService.download(doc._id);

      // Crea un link per il download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore download:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  const fileIcons = {
    pdf: 'text-red-500',
    doc: 'text-blue-500',
    docx: 'text-blue-500',
    jpg: 'text-green-500',
    jpeg: 'text-green-500',
    png: 'text-green-500',
    gif: 'text-purple-500',
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-foreground">I miei documenti</h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          {user.guestName && `Documenti relativi a ${user.guestName}`}
        </p>
      </div>

      {/* Info ospite */}
      {user.guestName && (
        <div className="bg-card rounded-lg border border-border p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold mb-2">Informazioni ospite</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs sm:text-sm">Nome ospite</p>
              <p className="font-medium">{user.guestName}</p>
            </div>
            {user.guestRoom && (
              <div>
                <p className="text-muted-foreground text-xs sm:text-sm">Stanza</p>
                <p className="font-medium">{user.guestRoom}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Documenti da firmare */}
      {pendingSignatures.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold text-amber-800 dark:text-amber-200">
              Documenti da firmare ({pendingSignatures.length})
            </h2>
          </div>
          <div className="space-y-2">
            {pendingSignatures.map((doc) => (
              <div
                key={doc._id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-white dark:bg-card rounded-lg border border-amber-100 dark:border-amber-800/50"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate text-sm sm:text-base">{doc.title}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {doc.signatureType === 'acknowledgment' && 'Presa visione'}
                    {doc.signatureType === 'approval' && 'Approvazione'}
                    {doc.signatureType === 'consent' && 'Consenso'}
                    {doc.signatureDeadline && (
                      <span>
                        {' '}• Scadenza: {new Date(doc.signatureDeadline).toLocaleDateString('it-IT')}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => setSignDialog({ open: true, document: doc })}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 active:bg-primary/80 w-full sm:w-auto min-h-[44px] touch-manipulation"
                >
                  <PenLine size={16} />
                  <span>Firma</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Azioni */}
      <div className="flex gap-3 sm:gap-4">
        <Link
          to="/my-documents/upload"
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 active:bg-primary/80 min-h-[44px] touch-manipulation w-full sm:w-auto"
        >
          <Upload size={18} />
          Carica documento
        </Link>
      </div>

      {/* Lista documenti */}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-3 sm:p-4 border-b border-border">
          <h2 className="text-base sm:text-lg font-semibold">Documenti recenti</h2>
        </div>

        {documents.length === 0 ? (
          <div className="p-6 sm:p-8 text-center">
            <FileText className="mx-auto text-muted-foreground mb-4" size={40} />
            <p className="text-muted-foreground text-sm sm:text-base">
              Non ci sono ancora documenti.
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground mt-2">
              I documenti caricati dall'amministrazione appariranno qui.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {documents.map((doc) => (
              <div
                key={doc._id}
                className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-accent/50 active:bg-accent/50"
              >
                <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                  <div
                    className={`p-2 rounded-lg bg-muted flex-shrink-0 ${
                      fileIcons[doc.fileType] || 'text-muted-foreground'
                    }`}
                  >
                    <File size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm sm:text-base truncate">{doc.title}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {doc.fileType.toUpperCase()} • {doc.fileSizeFormatted} •{' '}
                      {new Date(doc.createdAt).toLocaleDateString('it-IT')}
                    </p>
                    {doc.description && (
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1 line-clamp-2">
                        {doc.description}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(doc)}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-primary/10 text-primary rounded-lg hover:bg-primary/20 active:bg-primary/20 w-full sm:w-auto min-h-[44px] touch-manipulation"
                  title="Scarica documento"
                >
                  <Download size={16} />
                  <span>Scarica</span>
                </button>
              </div>
            ))}
          </div>
        )}

        {documents.length > 0 && (
          <div className="p-3 sm:p-4 border-t border-border">
            <Link
              to="/my-documents"
              className="text-sm text-primary hover:underline active:underline py-1 inline-block touch-manipulation"
            >
              Vedi tutti i documenti →
            </Link>
          </div>
        )}
      </div>

      {/* Dialog firma documento */}
      <SignDocumentDialog
        document={signDialog.document}
        open={signDialog.open}
        onClose={() => setSignDialog({ open: false, document: null })}
        onSigned={() => {
          loadPendingSignatures();
          loadDocuments();
        }}
      />
    </div>
  );
}

export default ClientDashboard;
